import { useEffect, useState } from 'react';
import { RUNTIME_URI } from '../../../constants/platform';
import { getActivatedForEnvironment } from '../../../sources/flow';
import translations from '../../../translations';
import type { Environment, EnvironmentFlow } from '../../../types/environment';
import type { FlowResponseAPI } from '../../../types/flow';
import { isPublic } from '../../../utils/flow';
import { isNullOrUndefined } from '../../../utils/guard';
import { SecurityIcon } from '../../flow/SecurityIcon';
import CopyableText from '../../generic/CopyableText';
import SearchInput from '../../generic/SearchInput';
import Table, { type TableColumnList } from '../../generic/Table';
import FlowIcon from '../../icons/FlowIcon';
import { useTenant } from '../../TenantProvider';
import { useEnvironments } from './EnvironmentsProvider';

type Props = {
    environment: Environment | null;
};

const EnvironmentFlows = ({ environment }: Props) => {
    const { getEnvironmentFlows, environmentFlowsLoading } = useEnvironments();
    const tenant = useTenant();

    const [flows, setFlows] = useState<EnvironmentFlow[]>([]);
    const [activatedFlows, setActivatedFlows] = useState<FlowResponseAPI[]>([]);
    const [page, setPage] = useState(1);
    const pageSize = 20;
    const [total, setTotal] = useState<number>(0);
    const [searchQuery, setSearchQuery] = useState('');

    // biome-ignore lint/correctness/useExhaustiveDependencies: Requires dedicated refactor to include all dependencies
    useEffect(() => {
        if (environment) {
            loadEnvironmentFlows(1, null);
            loadActivatedFlows();
        }
    }, [environment]);

    async function loadEnvironmentFlows(page: number, term: string | null) {
        if (environment) {
            const params = {
                environmentId: environment.id,
                page: page ?? 1,
                pageSize,
                searchTerm: term,
            };

            const response = await getEnvironmentFlows(params);

            setFlows(response?.items || []);
            setTotal(response?._meta.total || 0);
        }
    }

    async function loadActivatedFlows() {
        if (environment) {
            try {
                const activatedFlowsResponse = await getActivatedForEnvironment(environment.id);
                setActivatedFlows(activatedFlowsResponse || []);
            } catch (error) {
                console.error('Failed to load activated flows:', error);
                setActivatedFlows([]);
            }
        }
    }

    const onSearch = (term: string) => {
        setPage(1);
        setSearchQuery(term);
        loadEnvironmentFlows(1, term);
    };

    function getFlowUrl(flowId: string) {
        let url = '';
        if (!tenant.tenantSettings?.themes) {
            url = `${RUNTIME_URI}/${tenant.id}/play/${environment?.defaultPlayerName}?flow-id=${flowId}&environment-id=${environment?.id}`;
        } else if (tenant.tenantSettings?.themes) {
            url = `${RUNTIME_URI}/${tenant.id}/play/theme/${environment?.defaultThemeName}?flow-id=${flowId}&environment-id=${environment?.id}`;
        }
        return url;
    }

    function getActivatedFlow(flowId: string): FlowResponseAPI | undefined {
        return activatedFlows.find((flow) => flow.id.id === flowId);
    }

    const columns: TableColumnList<EnvironmentFlow> = [
        {
            renderHeader: () => 'Type',
            renderCell: ({ item }) => {
                if (!isNullOrUndefined(item)) {
                    return <FlowIcon width="20" height="20" />;
                }

                return null;
            },
            size: '60px',
        },
        {
            renderHeader: () => 'Name',
            renderCell: ({ item }) => item.name,
        },
        {
            renderHeader: () => 'Version ID',
            renderCell: ({ item }) => {
                const activatedFlow = getActivatedFlow(item.flowId);
                return (
                    <div className="flex gap-x-small align-center">
                        {activatedFlow && (
                            <SecurityIcon
                                isPublic={isPublic(activatedFlow)}
                                testId={`auth-${activatedFlow.id.id || activatedFlow.id.versionId}`}
                                label={
                                    isPublic(activatedFlow)
                                        ? translations.FLOW_auth_public_label
                                        : translations.FLOW_auth_secured_label
                                }
                            />
                        )}
                        <CopyableText
                            copyableText={item.versionId}
                            hasCopyButton={true}
                            wrapperClassName="overflow-hidden"
                        />
                    </div>
                );
            },
        },
        {
            renderHeader: () => 'Deployment Notes',
            renderCell: ({ item }) => item.summary,
            size: '35rem',
        },
        {
            renderHeader: () => 'Last Modified',
            renderCell: ({ item }) => {
                return new Date(item.updatedAt).toLocaleString();
            },
        },
        {
            renderHeader: () => 'Run',
            renderCell: ({ item }) => {
                const flowUrl = getFlowUrl(item.flowId);
                return (
                    <a
                        className="btn btn-primary"
                        href={flowUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        {translations.ENVIRONMENT_run_in + environment?.name}
                    </a>
                );
            },
        },
    ];

    return (
        <>
            <SearchInput className="margin-top" onChange={onSearch} value={searchQuery} />

            <Table
                wrapperClassName="table margin-top margin-bottom"
                columns={columns}
                items={flows}
                isLoading={environmentFlowsLoading}
                pagination={{
                    page,
                    total,
                    pageSize,
                    changePage: (nextPage) => {
                        setPage(nextPage);
                        loadEnvironmentFlows(nextPage, null);
                    },
                }}
            />
        </>
    );
};

export default EnvironmentFlows;
